<template>
  <div
    class="fixed inset-0 bg-overlay z-50 overflow-y-auto overscroll-contain"
  >
    <div class="flex min-h-full justify-center items-center p-2 xl:p-0">
      <div
        class="relative flex flex-col w-auto custom-820:w-auto xl:w-[903px] h-fit rounded bg-white custom-820:overflow-visible xl:overflow-hidden"
      >
      <!-- intro -->
      <div
        class="relative flex flex-row justify-start p-2 custom-820:p-[20px] xl:p-[54px] bg-primary-700"
      >
        <!-- close btn -->
        <button
          class="absolute left-[5px] top-[7px] custom-820:left-[20px] custom-820:top-[20px] xl:left-[25px] xl:top-[27px] w-9 h-9 rounded flex justify-center items-center text-white"
          @click="closeModal"
        >
          <span class="material-symbols-rounded">close</span>
        </button>
        <!-- sale title. The modal can be opened from the sale tabs, where the
             user never saw the sale's card, so it names the sale it is about
             to open. The left padding keeps long names off the close button. -->
        <div
          class="flex flex-row items-start gap-[12px] custom-820:gap-[20px] pl-[36px] xl:pl-[16px]"
        >
          <div
            class="flex flex-none justify-center items-center h-[40px] w-[40px] bg-complementary-600 text-complementary-900 rounded-full"
          >
            <span class="material-symbols-rounded">storefront</span>
          </div>
          <div class="flex flex-col">
            <template v-if="saleTitle">
              <p class="text-sm text-grays-200 font-regular">פתיחת הזמנה</p>
              <h2 class="font-bold text-xl custom-820:text-2xl text-white">
                {{ saleTitle }}
              </h2>
              <p class="text-base text-grays-200 font-light">
                {{ saleDescription }}
              </p>
            </template>
            <h2 v-else class="font-medium text-2xl text-white">פתיחת הזמנה</h2>
          </div>
        </div>
      </div>
      <!-- content -->
      <div class="flex flex-col px-[20px] xl:px-[53px]">
        <!-- sale details: the same facts as the card on the sales board, for
             users who reached this modal without passing through that card -->
        <div
          v-if="sale"
          class="flex flex-col md:flex-row md:flex-wrap gap-[12px] md:gap-x-[40px] md:gap-y-[16px] border border-grays-400 rounded bg-gray_semantic px-[21px] py-[14px] mt-[10px] custom-820:mt-[20px] xl:mt-[40px]"
        >
          <div v-if="sale.supply_begin" class="flex flex-col">
            <p class="text-sm font-medium text-grays-600">יום איסוף</p>
            <p class="text-base font-regular text-grays-900">
              {{ moment(sale.supply_begin).format("dddd") }}
            </p>
            <p class="text-base font-regular text-grays-900">
              {{ jDate(sale.supply_begin) }}
              {{ moment(sale.supply_begin).format("DD/MM/Y") }}
            </p>
            <div
              v-if="sale.day_diff"
              class="w-fit bg-error text-white text-sm font-medium px-[8px] py-[3px] rounded mt-[4px]"
            >
              <span class="material-symbols-rounded text-xs">error</span>
              שינוי ביום
            </div>
          </div>
          <div v-if="hasHours" class="flex flex-col">
            <p class="text-sm font-medium text-grays-600">שעה</p>
            <p
              v-if="hasTime(sale.general_start) || hasTime(sale.general_end)"
              class="text-base font-regular text-grays-900"
            >
              כללי {{ hours(sale.general_start, sale.general_end) }}
            </p>
            <p
              v-if="hasTime(sale.women_start) || hasTime(sale.women_end)"
              class="text-base font-regular text-grays-900"
            >
              נשים {{ hours(sale.women_start, sale.women_end) }}
            </p>
            <p
              v-if="hasTime(sale.men_start) || hasTime(sale.men_end)"
              class="text-base font-regular text-grays-900"
            >
              גברים {{ hours(sale.men_start, sale.men_end) }}
            </p>
            <div
              v-if="sale.time_diff"
              class="w-fit bg-error text-white text-sm font-medium px-[8px] py-[3px] rounded mt-[4px]"
            >
              <span class="material-symbols-rounded text-xs">error</span>
              שינוי בשעה
            </div>
          </div>
          <div v-if="sale.site" class="flex flex-col">
            <p class="text-sm font-medium text-grays-600">מיקום</p>
            <p
              v-if="sale.site.name"
              class="text-base font-regular text-grays-900"
            >
              {{ sale.site.name }}
            </p>
            <p v-if="location" class="text-base font-regular text-grays-900">
              {{ location }}
            </p>
          </div>
          <div v-if="sale.end" class="flex flex-col">
            <p class="text-sm font-medium text-grays-600">המערכת נסגרת</p>
            <p class="text-base font-regular text-grays-900">
              {{ moment(sale.end).format("dddd") }} {{ jDate(sale.end) }}
              {{ moment(sale.end).format("DD/MM/Y") }}
            </p>
            <p class="text-base font-medium text-grays-900">
              בשעה {{ moment(sale.end).format("HH:mm") }}
            </p>
          </div>
        </div>
        <h3
          class="text-base xl:text-xl text-grays-900 font-medium pt-[10px] pb-[10px] custom-820:pt-[20px] custom-820:pb-[20px] xl:pt-[40px] xl:pb-[32px]"
        >
          רגע לפני שנתחיל בקניה
        </h3>
        <!-- payment options -->
        <div
          v-if="needCard"
          class="xl:flex flex-row justify-between items-center border border-grays-400 rounded bg-gray_semantic py-[10px] xl:py-0 px-[21px] gap-[32px]"
        >
          <div class="flex flex-row items-center gap-6 p-2 xl:p-0">
            <div class="text-sm text-grays-700">תשלום באמצעות</div>
            <div class="flex-row flex gap-2">
              <img
                alt="Cardol"
                class="h-3"
                src="@/assets/icons/logos/Cardcol.png"
              />
              <img alt="" class="h-3" src="@/assets/icons/logos/nedarim.svg" />
            </div>
          </div>
          <div class="xl:h-[92px] bg-grays-300 xl:w-[1px]"></div>
          <div class="py-[10px] sm:py-0 text-sm text-grays-700">
            כרטיס אשראי חילופי:
          </div>
          <form
            class="flex flex-row justify-between items-center gap-[12px]"
            type="submit"
          >
            <div class="flex flex-col gap-[4px]">
              <label class="text-grays-700 text-sm font-regular"
                >תשלום באמצעות</label
              >
              <select-payment
                :credit-cards="creditCards"
                :current-card="currentCard"
                :openUp="true"
                @closeModal="closeModal"
                @card-changed="handleChangeCard"
              />
            </div>
            <div class="flex flex-col gap-[4px]">
              <label class="text-grays-700 text-sm font-regular">תשלומים</label>
              <select-number
                :current-payments="currentPayments"
                :max-number="1"
                @payment-changed="handleChangePayment"
              />
            </div>
          </form>
        </div>
        <div
          :class="needCard ? '' : '!pt-0'"
          class="flex flex-col w-fit pt-[5px] pb-[5px] custom-820:pt-[20px] custom-820:pb-[20px] xl:pt-[40px] xl:pb-[40px] gap-[2px] xl:gap-[24px]"
        >
          <div
            class="flex flex-row gap-[9px] text-error"
            v-if="sale && sale.special_msg_txt"
          >
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p class="font-medium text-sm md:text-base">
              {{ sale.special_msg_txt }}
            </p>
          </div>
          <div
            class="flex flex-row gap-[9px]"
            v-if="sale && sale.site && sale.site.note"
          >
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full text-complementary-900"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p class="text-grays-900 font-medium text-sm md:text-base">
              {{ sale.site.note }}
            </p>
          </div>
          <!-- "התראה בפתיחת הזמנה" of the categories this sale offers
               (open_sales → category_alerts, red ones first) -->
          <div
            v-for="(alert, index) in categoryAlerts"
            :key="`category-alert-${index}`"
            class="flex flex-row gap-[9px]"
            :class="alert.important ? 'text-error' : ''"
          >
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full"
              :class="alert.important ? '' : 'text-complementary-900'"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p
              class="font-medium text-sm md:text-base whitespace-pre-line"
              :class="alert.important ? '' : 'text-grays-900'"
            >
              {{ alert.text }}
            </p>
          </div>
          <div class="flex flex-row gap-[9px]">
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full text-complementary-900"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p class="text-grays-900 font-medium text-sm md:text-base">
              מזכירים לכם, בעת ביצוע ההזמנה הנכם מתחייבים לבוא בזמן לנקודת
              האיסוף שבחרתם ולאסוף את ההזמנה תוך שמירה על הניקיון והסדר במקום.
            </p>
          </div>
          <div class="flex flex-row gap-[9px]">
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full text-complementary-900"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p class="text-grays-900 font-medium text-sm md:text-base">
              לא נוכל לזכות אתכם במקרים של שכחה או טעות מצידכם.
            </p>
          </div>
          <div class="flex flex-row gap-[9px]">
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full text-complementary-900"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p class="text-grays-900 font-medium text-sm md:text-base">
              גם אם קרה משהו ועזבתם את האתר בפתאומיות, ההזמנה תשמר ותחוייב
              באשראי.
            </p>
          </div>
          <div class="flex flex-row gap-[9px]">
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full text-complementary-900"
            >
              <span class="material-symbols-rounded text-xl">error</span>
            </div>
            <p class="text-grays-900 font-medium text-sm md:text-base">
              בכל הזמנה תחויבו בדמי אחזקת תחנה.
            </p>
          </div>
          <div class="flex flex-row gap-[9px]" v-if="nc_need_confirm > 0">
            <div
              class="flex justify-center items-center h-[24px] w-[24px] rounded-full text-complementary-900"
            >
              <span class="material-symbols-rounded text-xl text-error"
                >error</span
              >
            </div>
            <!-- <p class="font-bold">
              במסגרת ההסכם של משנת יוסף עם כרטיסי "מכובד", בכרטיסי "נדרים קארד"
              ניתן לנצל 96%
              <br />
              100 ש"ח בכרטיס = 96 ש"ח במשנת יוסף
            </p> -->
          </div>
        </div>
      </div>
      <!-- action -->
      <div
        class="flex flex-col gap-[8px] pt-[10px] pb-[10px] custom-820:pt-[20px] custom-820:pb-[20px] xl:pt-[52px] xl:pb-[32px] pl-[10px] xl:pl-[43px] border-t border-grays-400"
      >
        <div
          v-if="errorMessage"
          class="flex flex-row gap-[9px] text-error justify-end"
        >
          <span class="material-symbols-rounded text-xl">error</span>
          <p class="font-medium text-sm">{{ errorMessage }}</p>
        </div>
        <div class="flex flex-row justify-end gap-1 xl:gap-[32px]">
          <check-box v-model="accepted">
            <slot> בסדר, מאשרים.</slot>
          </check-box>
          <button-primary
            :disabled="!accepted || (needCard && !creditCards.length)"
            @click="createOrder"
          >
            {{ isLoading ? "טוען נתונים..." : "להתחיל להזמין" }}
          </button-primary>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import "moment/dist/locale/he";
import axiosInstance from "../../helpers/axios.js";
import { formatJDate } from "@/helpers/dateUtils.js";
import { saleName } from "@/helpers/saleUtils.js";
import ButtonPrimary from "@/components/Global/ButtonPrimary.vue";
import CheckBox from "@/components/Global/CheckBox.vue";
import SelectNumber from "@/components/Global/SelectNumber.vue";
import SelectPayment from "@/components/Global/SelectPayment.vue";

export default {
  name: "modal-agreement",
  components: { SelectPayment, SelectNumber, CheckBox, ButtonPrimary },
  data() {
    return {
      accepted: false,
      isLoading: false,
      creditCards: [],
      currentCard: { id: "", save_card: "**** **** **** ****" },
      currentPayments: 9,
      needCard: true,
      nc_need_confirm: 0,
      errorMessage: "",
    };
  },
  methods: {
    closeModal() {
      this.$store.dispatch("order/toggleShowAgreement");
    },
    moment(param1, param2) {
      return moment(param1, param2);
    },
    jDate(date) {
      return formatJDate(date);
    },
    // Hour columns come back as "00:00:00" when the station has no such slot.
    hasTime(time) {
      return !!time && time !== "00:00:00";
    },
    hours(start, end) {
      return [start, end]
        .filter((time) => this.hasTime(time))
        .map((time) => moment(time, "HH:mm:ss").format("HH:mm"))
        .join(" - ");
    },
    async handleChangeCard(card) {
      this.currentCard = card;
    },
    async handleChangePayment(paymentsNr) {
      this.currentPayments = paymentsNr;
    },
    async createOrder() {
      this.errorMessage = "";
      this.isLoading = true;
      try {
        const paymentData = {
          collectionMethod: false,
          siteID: "",
          tas: this.needCard ? this.currentPayments.toString() : undefined,
          card_id: this.needCard ? this.currentCard.id : undefined,
        };
        const response = await axiosInstance.post(
          `/create_order/${this.saleID}`,
          paymentData
        );
        this.$store.dispatch("order/toggleShowAgreement");
        this.$router
          .push(`/order/${response.data.order.id}/${this.saleID}`)
          .then(() => {
            window.location.reload();
          });
      } catch (e) {
        console.error(e);
        if (e.response?.status === 409 && e.response?.data?.order?.id) {
          // The client already holds an order for this parent sale at another
          // station: continue that order, under its own sale (categories and
          // favourites are keyed by the route's saleID).
          const existing = e.response.data.order;
          this.$store.dispatch("order/toggleShowAgreement");
          this.$router
            .push(`/order/${existing.id}/${existing.saleID || this.saleID}`)
            .then(() => {
              window.location.reload();
            });
        } else {
          this.errorMessage =
            e.response?.data?.message || "אירעה שגיאה, אנא נסו שוב";
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getCards() {
      const response = await axiosInstance.get("/payment_methods/cards");
      this.creditCards = response.data.filter((card) => card.type === "cc");
      if (this.creditCards.length) {
        // Preselect the client's chosen default, not the first card the API
        // returns: the list comes back by id, so [0] was the oldest saved
        // card and every order of a multi-card client was created on it.
        const preferred =
          this.creditCards.find((card) => card.is_default) ||
          this.creditCards[0];
        await this.handleChangeCard(preferred);
      }
      this.nc_need_confirm = response.data.filter(
        (item) => item.must_confirm === 1 && item.type == "nc"
      ).length;
    },
  },
  computed: {
    saleID() {
      return this.$store.state.order.saleID;
    },
    sale() {
      return this.$store.state.order.sale;
    },
    saleTitle() {
      return saleName(this.sale);
    },
    // Category alerts the API attached to the sale; an older payload has none.
    categoryAlerts() {
      const alerts = this.sale?.category_alerts;
      return Array.isArray(alerts) ? alerts : [];
    },
    // Same fallback as the card on the sales board.
    saleDescription() {
      const description = this.sale?.parent?.description;
      return description && description.trim()
        ? description
        : "המכירה השבועית";
    },
    hasHours() {
      const sale = this.sale || {};
      return [
        sale.general_start,
        sale.general_end,
        sale.women_start,
        sale.women_end,
        sale.men_start,
        sale.men_end,
      ].some((time) => this.hasTime(time));
    },
    // A one-off address for this sale wins over the station's own address.
    location() {
      const sale = this.sale || {};
      const site = sale.site || {};
      if (sale.special_address) {
        return [sale.special_address, site.city].filter(Boolean).join(" ");
      }
      const streetCity = [site.street, site.city].filter(Boolean).join(", ");
      return [streetCity ? `${streetCity}.` : "", site.address_description]
        .filter(Boolean)
        .join(" ");
    },
  },
  mounted() {
    this.getCards();
    this.needCard = !!this.$store.state.order.isOnlyCard;
    // הפופאפ עצמו גולל, לכן חוסמים את גלילת הדף שמאחוריו
    document.body.style.overflow = "hidden";
  },
  unmounted() {
    document.body.style.overflow = "";
  },
};
</script>


<style>
</style>